import React, {useCallback, useState} from "react";
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	TextField
} from "@mui/material";

/**
 * Sustitutos de `confirm()` y `prompt()` del navegador, que bloquean el hilo, no se pueden
 * estilar y desentonan con el resto del admin.
 *
 * Cada hook devuelve la función a llamar (que resuelve una promesa con la respuesta) y el
 * elemento del diálogo, que hay que pintar una vez en el componente.
 *
 * @example
 * const {confirm, confirmDialog} = useConfirm();
 * ...
 * if (await confirm({message: "¿Seguro?"})) borrar();
 * ...
 * return <>{confirmDialog}...</>;
 */

export interface ConfirmOptions {
	title?: string;
	message: React.ReactNode;
	confirmLabel?: string;
	cancelLabel?: string;
	/** Pinta el botón de aceptar en rojo, para acciones destructivas. */
	danger?: boolean;
}

interface ConfirmRequest extends Required<Omit<ConfirmOptions, "message">> {
	message: React.ReactNode;
	resolve: (accepted: boolean) => void;
}

export function useConfirm() {

	const [request, setRequest] = useState<ConfirmRequest | undefined>(undefined);

	const confirm = useCallback((options: ConfirmOptions): Promise<boolean> => {
		return new Promise<boolean>((resolve) => setRequest({
			title: options.title ?? "Confirma la operación",
			message: options.message,
			confirmLabel: options.confirmLabel ?? "Aceptar",
			cancelLabel: options.cancelLabel ?? "Cancelar",
			danger: options.danger ?? false,
			resolve
		}));
	}, []);

	const close = (accepted: boolean) => {
		request?.resolve(accepted);
		setRequest(undefined);
	};

	const confirmDialog = (
		<Dialog open={!!request} onClose={() => close(false)} maxWidth={"sm"} fullWidth>
			<DialogTitle>{request?.title}</DialogTitle>
			<DialogContent>
				<DialogContentText component={"div"}>{request?.message}</DialogContentText>
			</DialogContent>
			<DialogActions>
				<Button onClick={() => close(false)}>{request?.cancelLabel}</Button>
				<Button variant={"contained"}
						color={request?.danger ? "error" : "primary"}
						onClick={() => close(true)}
						autoFocus>
					{request?.confirmLabel}
				</Button>
			</DialogActions>
		</Dialog>
	);

	return {confirm, confirmDialog};
}

export interface PromptOptions {
	title?: string;
	message?: React.ReactNode;
	label: string;
	defaultValue?: string;
	confirmLabel?: string;
	/** Devuelve el mensaje de error si el valor no vale, o undefined si es correcto. */
	validate?: (value: string) => string | undefined;
}

interface PromptRequest extends PromptOptions {
	resolve: (value: string | undefined) => void;
}

export function usePrompt() {

	const [request, setRequest] = useState<PromptRequest | undefined>(undefined);
	const [value, setValue] = useState<string>("");
	const [error, setError] = useState<string | undefined>(undefined);

	const prompt = useCallback((options: PromptOptions): Promise<string | undefined> => {
		setValue(options.defaultValue ?? "");
		setError(undefined);
		return new Promise<string | undefined>((resolve) => setRequest({...options, resolve}));
	}, []);

	const cancel = () => {
		request?.resolve(undefined);
		setRequest(undefined);
	};

	const accept = () => {
		const problem = request?.validate?.(value);
		if (problem) {
			setError(problem);
			return;
		}
		request?.resolve(value);
		setRequest(undefined);
	};

	const promptDialog = (
		<Dialog open={!!request} onClose={cancel} maxWidth={"sm"} fullWidth>
			<DialogTitle>{request?.title ?? "Indica un valor"}</DialogTitle>
			<DialogContent>
				{request?.message && (
					<DialogContentText component={"div"} sx={{mb: 2}}>{request.message}</DialogContentText>
				)}
				<TextField autoFocus
						   fullWidth
						   size={"small"}
						   label={request?.label}
						   value={value}
						   error={!!error}
						   helperText={error}
						   onChange={(e) => {
							   setValue(e.target.value);
							   setError(undefined);
						   }}
						   onKeyDown={(e) => {
							   if (e.key === "Enter") {
								   e.preventDefault();
								   accept();
							   }
						   }}/>
			</DialogContent>
			<DialogActions>
				<Button onClick={cancel}>Cancelar</Button>
				<Button variant={"contained"} onClick={accept}>{request?.confirmLabel ?? "Aceptar"}</Button>
			</DialogActions>
		</Dialog>
	);

	return {prompt, promptDialog};
}
